import type { Branding, RuntimeConfig } from './types';
import { DEFAULT_TELEMETRY_SAMPLE_PERCENT, ENVIRONMENT_CONFIGS } from './environments';

let config: RuntimeConfig | null = null;

const BRANDINGS: readonly Branding[] = ['praxes', 'rmi'];

interface EnvVarOverrides {
  legacyApiBaseUrl?: string;
  apiBaseUrl?: string;
  branding?: Branding;
  telemetrySamplePercent?: number;
}

/**
 * Load runtime configuration from hostname detection or environment variables.
 *
 * Environment variables take precedence (for bespoke builds):
 * - VITE_LEGACY_BASE_URL or VITE_LEGACY_API_BASE_URL
 * - VITE_EREBUS_BASE_URL or VITE_API_BASE_URL
 * - VITE_BRANDING
 * - VITE_TELEMETRY_SAMPLE_PERCENT
 *
 * @param overrides - Optional manual overrides for testing
 * @returns RuntimeConfig object
 */
export function loadRuntimeConfig(overrides?: EnvVarOverrides): RuntimeConfig {
  if (config) {
    return config;
  }

  // Check for environment variable overrides (Vite injects these at build time)
  const envLegacyUrl = getEnvVar('VITE_LEGACY_BASE_URL') || getEnvVar('VITE_LEGACY_API_BASE_URL');
  const envApiUrl = getEnvVar('VITE_EREBUS_BASE_URL') || getEnvVar('VITE_API_BASE_URL');
  // Apply overrides (env vars or manual)
  const legacyOverride = overrides?.legacyApiBaseUrl || envLegacyUrl;
  const apiOverride = overrides?.apiBaseUrl || envApiUrl;
  const brandingOverride = resolveBranding(overrides?.branding);
  const samplePercentOverride = resolveTelemetrySamplePercent(overrides?.telemetrySamplePercent);

  // If both env vars are set, use them directly
  if (legacyOverride && apiOverride) {
    config = {
      legacyApiBaseUrl: legacyOverride,
      apiBaseUrl: apiOverride,
      branding: brandingOverride || 'praxes',
      environment: 'unknown',
      callerRatingMandatory: false,
      useCookieAuth: false,
      connectUseCookieAuth: false,
      telemetrySamplePercent: samplePercentOverride ?? DEFAULT_TELEMETRY_SAMPLE_PERCENT,
    };
    return config;
  }

  // Auto-detect from hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const envConfig = ENVIRONMENT_CONFIGS.find((env) => env.pattern.test(hostname));

  if (envConfig) {
    config = {
      legacyApiBaseUrl: legacyOverride || envConfig.legacyApiBaseUrl,
      apiBaseUrl: apiOverride || envConfig.apiBaseUrl,
      branding: brandingOverride || envConfig.branding,
      environment: envConfig.environment,
      callerRatingMandatory: envConfig.callerRatingMandatory,
      useCookieAuth: envConfig.useCookieAuth,
      connectUseCookieAuth: envConfig.connectUseCookieAuth,
      telemetrySamplePercent: samplePercentOverride ?? envConfig.telemetrySamplePercent,
    };
  } else {
    // Unknown environment - warn and use safe defaults
    console.warn(`[runtime-config] Unknown hostname: ${hostname}. Using localhost defaults.`);
    config = {
      legacyApiBaseUrl: legacyOverride || 'http://localhost:8080',
      apiBaseUrl: apiOverride || 'http://localhost:5050',
      branding: brandingOverride || 'praxes',
      environment: 'unknown',
      callerRatingMandatory: false,
      useCookieAuth: false,
      connectUseCookieAuth: false,
      telemetrySamplePercent: samplePercentOverride ?? DEFAULT_TELEMETRY_SAMPLE_PERCENT,
    };
  }

  return config;
}

/**
 * Get the current runtime configuration.
 * Automatically loads config if not already loaded.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!config) {
    return loadRuntimeConfig();
  }
  return config;
}

/**
 * Check if runtime config has been loaded.
 */
export function isConfigLoaded(): boolean {
  return config !== null;
}

/**
 * Reset config (primarily for testing).
 */
export function resetConfig(): void {
  config = null;
}

/**
 * Resolve a branding override, from an explicit override or VITE_BRANDING.
 *
 * Branding normally comes from the hostname, which means the white-label look
 * and its `-rmi` locales can only be exercised by owning that hostname. This
 * override exists so a bespoke build — or a developer on localhost — can run
 * another brand while still pointing at local APIs.
 *
 * VITE_BRANDING is read at build time, so it is a deploy-time decision and
 * cannot be flipped by a visitor. An unrecognised value is ignored rather than
 * trusted, so a typo falls back to hostname detection instead of producing a
 * half-branded app.
 */
function resolveBranding(override?: Branding): Branding | undefined {
  const value = override || getEnvVar('VITE_BRANDING');
  if (!value) {
    return undefined;
  }
  if ((BRANDINGS as readonly string[]).includes(value)) {
    return value as Branding;
  }
  console.warn(
    `[runtime-config] Ignoring unknown branding "${value}". Expected one of: ${BRANDINGS.join(', ')}.`,
  );
  return undefined;
}

/**
 * Resolve the telemetry sample percentage, from an explicit override or
 * VITE_TELEMETRY_SAMPLE_PERCENT.
 *
 * Read at build time like the other overrides, so it is a deploy-time decision
 * and a visitor cannot turn their own sampling up. An unusable value is ignored
 * rather than trusted: sampling at NaN percent would silently send nothing, and
 * a typo should not quietly switch the aggregate view off.
 */
function resolveTelemetrySamplePercent(override?: number): number | undefined {
  const raw = override ?? getEnvVar('VITE_TELEMETRY_SAMPLE_PERCENT');
  if (raw === undefined || raw === '') {
    return undefined;
  }

  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0 || value > 100) {
    console.warn(
      `[runtime-config] Ignoring unusable telemetry sample percent "${raw}". Expected a number from 0 to 100.`,
    );
    return undefined;
  }

  return value;
}

/**
 * Safely get environment variable (works in both Node and browser with Vite).
 */
function getEnvVar(name: string): string | undefined {
  // Vite injects env vars via import.meta.env. Type it locally so consumers
  // (e.g. @erebus/ui) don't need the ambient vite/client types.
  const meta = import.meta as unknown as { env?: Record<string, string> };
  if (meta.env) {
    return meta.env[name];
  }
  // Node.js fallback
  if (typeof process !== 'undefined' && process.env) {
    return process.env[name];
  }
  return undefined;
}
